import type { VehicleScheduleContext } from '@/app/utils/vehicleScheduleContext';

export interface DerivationFingerprintInput {
    productId: string | null;
    resolvedPlanId: string | null;
    vehicleContext: VehicleScheduleContext;
}

export type DerivationContext = Omit<DerivationFingerprintInput, 'resolvedPlanId'>;

// Every field that reaches derive()'s maths. Deliberately excludes ageYears and
// make/model/derivative/transmission/fuelType/engineSize (matcher-only).
const FINGERPRINT_VEHICLE_FIELDS = [
    'firstRegistrationDate',
    'vehicleMileage',
    'annualMileage',
    'lastServiceDate',
    'lastServiceMileage',
] as const;

export function derivationFingerprint({
    productId,
    resolvedPlanId,
    vehicleContext,
}: DerivationFingerprintInput): string {
    const parts: unknown[] = [productId, resolvedPlanId];
    for (const field of FINGERPRINT_VEHICLE_FIELDS) {
        parts.push(vehicleContext[field] ?? null);
    }
    return JSON.stringify(parts);
}

function storedResolvedPlanId(fingerprint: string): string | null | undefined {
    let parts: unknown;
    try {
        parts = JSON.parse(fingerprint);
    } catch {
        return undefined;
    }
    if (!Array.isArray(parts) || parts.length !== FINGERPRINT_VEHICLE_FIELDS.length + 2) {
        return undefined;
    }
    const stored: unknown = parts[1];
    return typeof stored === 'string' || stored === null ? stored : undefined;
}

// Row indices only mean anything against the plan they were stamped with, so a
// matcher-only vehicle switch is safe for the dates only if the plan re-resolves.
export function fingerprintPlanMatches(
    fingerprint: string,
    resolvedPlanId: string | null
): boolean {
    const stored = storedResolvedPlanId(fingerprint);
    return stored !== undefined && stored === resolvedPlanId;
}

// The resolved plan id lags a product or vehicle change by one derive, so it is
// substituted from the stored value: only a context change reads as changed.
export function derivationContextChanged(
    fingerprint: string,
    context: DerivationContext
): boolean {
    const resolvedPlanId = storedResolvedPlanId(fingerprint);
    if (resolvedPlanId === undefined) {
        return true;
    }
    return derivationFingerprint({ ...context, resolvedPlanId }) !== fingerprint;
}
